import * as React from 'react';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { PropertySource } from './property-source';

import { HotelViewModel } from 'models';

import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';

import { BadgeGenerator } from 'components/common/badge-generation';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingAmenities } from 'components/search/listing/internals/listing-amenities';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPropertyBadges } from './listing-property-badges';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { SearchListingPlaceholderLarge, SearchListingPlaceholderSmall } from './placeholders';
import { Stars } from './star-rating';
import { TertiaryBadge } from './tertiary-badge';
import { TitleHeading } from './title-heading';

interface DefaultListingProps {
  className?: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  defaultListingTopMarginClass?: string;
  stars?: number;
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  imageAspectRatio?: string;
  'data-stid'?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  displaySecondaryBadge?: boolean;
}

export const DefaultListing: React.FunctionComponent<DefaultListingProps> = ({
  index,
  loading,
  model,
  className,
  defaultListingTopMarginClass,
  stars,
  getTitleComponent,
  'data-stid': dataStid,
  cardLinkProps,
  listingClickHandler,
  setItemRef,
  imageAspectRatio,
  displaySecondaryBadge,
}) => {
  const {
    amenities,
    listingFooter,
    name,
    neighborhood,
    price,
    propertySummary,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    recentlyViewed,
    propertyDetailsLink,
    propertySource,
    pinnedDetails,
    propertyListingBadgeModel,
  } = model;

  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
    tag: 'li' as 'li',
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };
  const propertySourceProps = propertySource;

  const priceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    wrapPrice: true,
    viewSmall: true,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    index,
  };
  const pinned = Boolean(pinnedDetails?.heading);

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <Experiment name="Compact_SRP_Property_Card_mWeb">
          <ExperimentControl>
            <SearchListingPlaceholderSmall />
          </ExperimentControl>
          <ExperimentVariant bucket={1}>
            <SearchListingPlaceholderLarge />
          </ExperimentVariant>
          <ExperimentVariant bucket={2}>
            <SearchListingPlaceholderLarge />
          </ExperimentVariant>
        </Experiment>
      </li>
    );
  }

  const { secondary, tertiary } = { ...propertyListingBadgeModel };
  const secondaryIsSafe = secondary && (secondary.text || secondary.icon || secondary.mark);
  const tertiaryIsSafe = tertiary && (tertiary.text || tertiary.icon || tertiary.mark);

  return (
    // Typescript doesn't seem to be enforcing props for this Card. Ref is not a prop in this version of UitkCard.
    <UitkCard {...listingWrapperProps}>
      <ListingName visuallyHidden={true} />
      <div className="has-action listing__card">
        <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} pinnedDefaultListing={pinned} />
        {pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
        <UitkLayoutPosition type="relative">
          <ListingErrorBoundary thumbnail={thumbnail} name={name || ''}>
            {getTitleComponent(imageAspectRatio)}
          </ListingErrorBoundary>
          {tertiaryIsSafe && (
            <UitkLayoutPosition type="relative">
              <TertiaryBadge propertyListingBadgeModel={propertyListingBadgeModel} />
            </UitkLayoutPosition>
          )}
          <UitkLayoutPosition type="relative" cloneElement={true}>
            <UitkCardContentSection className={`listing-content ${defaultListingTopMarginClass}`}>
              {displaySecondaryBadge && secondaryIsSafe && (
                <UitkLayoutPosition aria-hidden={true} type="absolute" position={{ top: 'minus-four', right: 'two', zindex: 'layer3' }}>
                  <BadgeGenerator badge={secondary || {}} uniqueId={model.hotelId} />
                </UitkLayoutPosition>
              )}
              <ListingPropertyBadges />
              <ListingName lineCount={2} />
              <Stars stars={stars} />
              <PropertySource {...propertySourceProps} />
              <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
              <Neighborhood neighborhood={neighborhood} />
              <ListingAmenities amenities={amenities} />
              <UitkLayoutFlex justifyContent="space-between" direction="row">
                <UitkLayoutFlexItem alignSelf="end">
                  <div>
                    <ListingCommonSection />
                  </div>
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem alignSelf="end" maxWidth="half_width">
                  <div data-stid="content-hotel-price">
                    {displaySecondaryBadge && secondaryIsSafe && <div className="is-visually-hidden">{secondary?.text}</div>}
                    <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                  </div>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkCardContentSection>
          </UitkLayoutPosition>
          <ListingFooter {...cardLinkProps} listingFooter={listingFooter} listingClickHandler={listingClickHandler} />
        </UitkLayoutPosition>
      </div>
      <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
    </UitkCard>
  );
};

DefaultListing.displayName = 'DefaultListing';
